// src/components/admin/ActiveUsersWidget.js
import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  Avatar,
  Chip,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  LinearProgress,
  Divider,
  Badge
} from '@mui/material';
import {
  FiberManualRecord as OnlineIcon,
  Schedule as RecentIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  DirectionsRun as AthleteIcon,
  Work as OrganizerIcon,
  Timer as TimerIcon,
  School as CoachIcon,
  Gavel as JudgeIcon
} from '@mui/icons-material';

const ActiveUsersWidget = ({ data }) => {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 30000); // Actualizar cada 30 segundos

    return () => clearInterval(timer);
  }, []);

  if (!data) return null;

  const getRoleIcon = (role) => {
    const icons = {
      atleta: <AthleteIcon />,
      organizador: <OrganizerIcon />,
      cronometrador: <TimerIcon />,
      entrenador: <CoachIcon />,
      juez: <JudgeIcon />
    };
    return icons[role] || <PeopleIcon />;
  };

  const getRoleColor = (role) => {
    const colors = {
      atleta: 'success',
      organizador: 'primary',
      cronometrador: 'secondary',
      entrenador: 'info',
      juez: 'warning'
    };
    return colors[role] || 'default';
  };

  const getRoleName = (role) => {
    const names = {
      atleta: 'Atletas',
      organizador: 'Organizadores',
      cronometrador: 'Cronometradores',
      entrenador: 'Entrenadores',
      juez: 'Jueces'
    };
    return names[role] || role;
  };

  // Simular usuarios en línea recientes
  const recentOnlineUsers = [
    { id: 1, name: 'Ana García', role: 'atleta', lastSeen: '2024-12-09 14:45:00', status: 'online' },
    { id: 2, name: 'Carlos López', role: 'organizador', lastSeen: '2024-12-09 14:42:00', status: 'online' },
    { id: 3, name: 'María Rodríguez', role: 'entrenador', lastSeen: '2024-12-09 14:40:00', status: 'recent' },
    { id: 4, name: 'Juan Pérez', role: 'cronometrador', lastSeen: '2024-12-09 14:38:00', status: 'recent' },
    { id: 5, name: 'Laura Martín', role: 'atleta', lastSeen: '2024-12-09 14:35:00', status: 'recent' }
  ];

  // Calcular actividad por hora (simulado)
  const hourlyActivity = data.peakHours || [9, 12, 15, 18, 21];
  const currentHour = new Date().getHours();
  const isCurrentlyPeakHour = hourlyActivity.includes(currentHour);

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const past = new Date(timestamp);
    const diffMinutes = Math.floor((now - past) / (1000 * 60));
    
    if (diffMinutes < 1) return 'Ahora';
    if (diffMinutes < 60) return `Hace ${diffMinutes}m`;
    const diffHours = Math.floor(diffMinutes / 60);
    return `Hace ${diffHours}h`;
  };

  return (
    <Card sx={{ height: '100%' }}>
      <CardHeader
        title="👥 Usuarios Activos"
        subheader={`Actualizado: ${currentTime.toLocaleTimeString('es-ES')}`}
        action={
          <Box display="flex" alignItems="center" gap={1}>
            <Badge badgeContent={data.online} color="success">
              <OnlineIcon color="success" />
            </Badge>
            {isCurrentlyPeakHour && (
              <Chip 
                label="Hora Pico" 
                color="warning" 
                size="small"
                icon={<TrendingUpIcon />}
              />
            )}
          </Box>
        }
      />
      
      <CardContent>
        {/* Resumen de actividad */}
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'success.light', color: 'success.dark' }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            {data.online}
          </Typography>
          <Typography variant="body1">
            Usuarios en línea ahora
          </Typography>
          <Typography variant="caption">
            📈 {data.last24h} usuarios únicos en 24h
          </Typography>
        </Paper>

        {/* Distribución por rol */}
        <Typography variant="subtitle2" gutterBottom>
          👥 Usuarios Activos por Rol
        </Typography>
        
        <Grid container spacing={1} sx={{ mb: 3 }}>
          {Object.entries(data.byRole || {}).map(([role, count]) => (
            <Grid item xs={6} key={role}>
              <Box
                sx={{
                  p: 1.5,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <Avatar 
                  sx={{ 
                    bgcolor: `${getRoleColor(role)}.main`,
                    width: 28,
                    height: 28
                  }}
                >
                  {React.cloneElement(getRoleIcon(role), { fontSize: 'small' })}
                </Avatar>
                <Box flex={1}>
                  <Typography variant="body2" fontWeight="bold">
                    {count}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {getRoleName(role)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Actividad por horas pico */}
        <Typography variant="subtitle2" gutterBottom>
          ⏰ Horas de Mayor Actividad
        </Typography>
        
        <Box sx={{ mb: 3 }}>
          {hourlyActivity.map((hour, index) => {
            const isCurrentHour = hour === currentHour;
            const activity = Math.floor(Math.random() * 40) + 60; // Simular actividad
            
            return (
              <Box key={hour} sx={{ mb: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                  <Typography variant="body2">
                    {hour}:00 - {hour + 1}:00
                    {isCurrentHour && ' (Actual)'}
                  </Typography>
                  <Typography variant="body2" fontWeight="bold" color={isCurrentHour ? 'primary.main' : 'text.primary'}>
                    {activity}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={activity}
                  color={isCurrentHour ? 'primary' : 'default'}
                  sx={{ height: 6, borderRadius: 3 }}
                />
              </Box>
            );
          })}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Usuarios conectados recientemente */}
        <Typography variant="subtitle2" gutterBottom>
          🟢 Conectados Recientemente
        </Typography>
        
        <List dense sx={{ maxHeight: 200, overflow: 'auto' }}>
          {recentOnlineUsers.map((user) => (
            <ListItem key={user.id} sx={{ px: 0 }}>
              <ListItemAvatar>
                <Badge
                  overlap="circular"
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  badgeContent={
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: user.status === 'online' ? 'success.main' : 'warning.main',
                        border: 2,
                        borderColor: 'background.paper'
                      }}
                    />
                  }
                >
                  <Avatar 
                    sx={{ 
                      bgcolor: `${getRoleColor(user.role)}.main`,
                      width: 32,
                      height: 32
                    }}
                  >
                    {user.name[0]}
                  </Avatar>
                </Badge>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography variant="body2" fontWeight="bold">
                    {user.name}
                  </Typography>
                }
                secondary={
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Chip
                      label={getRoleName(user.role)}
                      color={getRoleColor(user.role)}
                      size="small"
                      sx={{ fontSize: '0.6rem', height: 16 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {formatTimeAgo(user.lastSeen)}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>

        {/* Estadísticas adicionales */}
        <Box sx={{ mt: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Box textAlign="center">
                <Typography variant="h6" color="primary.main">
                  {Math.round((data.online / data.last24h) * 100)}%
                </Typography>
                <Typography variant="caption">Tasa Conectividad</Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box textAlign="center">
                <Typography variant="h6" color="info.main">
                  4.2min
                </Typography>
                <Typography variant="caption">Sesión Promedio</Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ActiveUsersWidget;